import {Component, OnInit} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {RouterLink} from '@angular/router';
import {forkJoin} from 'rxjs';
import {SubmissionHttpService} from '../../forms/data-access/submission-http.service';
import {FormHttpService} from '../../forms/data-access/form-http.service';
import {Submission, SubmissionStatus} from '../../forms/shared/models/submission.model';
import {submissionStatusClass, submissionStatusLabel} from '../../forms/shared/services/submission-display';

/**
 * Employee self-view: a read-only list of the signed-in employee's own form submissions (the backend scopes
 * `GET /submission` to the caller). Shows the form title, status, when it was submitted, and any review flag +
 * deadline. Opening one shows its masked values ({@link EmployeeSubmissionDetailComponent}); employees cannot
 * reveal or download (no SensitiveDataAccessor role), so neither action is offered here.
 */
@Component({
  selector: 'app-employee-submissions',
  standalone: true,
  imports: [DatePipe, NgClass, RouterLink],
  template: `
    <div class="absolute inset-0 overflow-y-auto p-3 sm:p-4 lg:p-6">
      <div class="mx-auto w-full max-w-3xl flex flex-col gap-4">
        <h1 class="text-xl font-bold tracking-tight text-gray-900">Meine Formulare</h1>

        @if (loading) {
          <div class="flex items-center justify-center py-20">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else if (error) {
          <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm">
            <p class="text-sm font-semibold text-red-600">Ihre Formulare konnten nicht geladen werden.</p>
            <button (click)="load()"
                    class="mt-2 inline-flex items-center text-red-600 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">
              Erneut versuchen
            </button>
          </div>
        } @else if (submissions.length === 0) {
          <p class="text-sm text-gray-500 italic py-10 border-2 border-dashed border-gray-100 rounded-2xl text-center">
            Sie haben noch keine Formulare eingereicht.
          </p>
        } @else {
          <ul class="flex flex-col gap-3">
            @for (s of submissions; track s.id) {
              <li>
                <a [routerLink]="[s.id]"
                   class="block p-4 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <p class="text-base font-bold text-gray-800 tracking-tight truncate">{{ titleFor(s) }}</p>
                      <p class="mt-1 text-sm text-gray-500">
                        {{ s.submittedAt ? (s.submittedAt | date: 'dd.MM.yyyy HH:mm') : 'Entwurf' }}
                      </p>
                    </div>
                    <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                          [ngClass]="statusClass(s.status)">{{ statusLabel(s.status) }}</span>
                  </div>
                  <!-- Validity (#404): "gültig bis" is shown as soon as the form has a Gültigkeitsdauer, and turns
                       into an "abgelaufen" badge once the server says the answer is stale. Both values come from
                       the server's own SubmissionValidity helper — nothing is re-derived here beyond formatting. -->
                  <div class="mt-2 flex flex-wrap items-center gap-2">
                    @if (s.needsReview) {
                      <span class="inline-flex items-center rounded-full bg-yellow-50 px-2.5 py-0.5 text-xs font-semibold text-yellow-700">
                        Prüfung nötig@if (s.reviewDeadline) {<span>&nbsp;· fällig am {{ s.reviewDeadline | date: 'dd.MM.yyyy' }}</span>}
                      </span>
                    }
                    @if (s.expired) {
                      <span data-test="expired-badge"
                            class="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700">abgelaufen</span>
                    } @else if (s.expiresAt) {
                      <span data-test="valid-until"
                            class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">gültig bis {{ s.expiresAt | date: 'dd.MM.yyyy' }}</span>
                    }
                  </div>
                </a>
              </li>
            }
          </ul>
        }
      </div>
    </div>
  `
})
export class EmployeeSubmissionsComponent implements OnInit {
  protected loading = true;
  protected error = false;
  protected submissions: Submission[] = [];
  /** formId → form title, so rows show a name instead of a hex id. */
  private titles: Record<string, string> = {};

  protected readonly statusLabel = submissionStatusLabel;
  protected readonly statusClass = submissionStatusClass;

  constructor(private http: SubmissionHttpService, private forms: FormHttpService) {
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = false;
    forkJoin({list: this.http.list(), defs: this.forms.listDefinitions()}).subscribe({
      next: ({list, defs}) => {
        this.titles = Object.fromEntries(defs.map(d => [d.id, d.title]));
        // Newest first. Dates arrive as Date objects (the global DateInterceptor revives ISO strings), so compare
        // by timestamp rather than string — works whether submittedAt is a Date or a string.
        const time = (v?: string | Date) => (v ? new Date(v).getTime() : 0);
        // Drafts are disabled across submissions (to be implemented later), so they are hidden here — only
        // finalised submissions show. Of those, collapse to the latest per form (one row per form, any version).
        const finalised = list.filter(s => s.status !== SubmissionStatus.Draft);
        const latestPerForm = new Map<string, Submission>();
        for (const s of finalised) {
          const current = latestPerForm.get(s.formId);
          if (!current || time(s.submittedAt) > time(current.submittedAt)) latestPerForm.set(s.formId, s);
        }
        this.submissions = [...latestPerForm.values()].sort((a, b) => time(b.submittedAt) - time(a.submittedAt));
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  protected titleFor(s: Submission): string {
    return this.titles[s.formId] ?? `Formular ${s.formId.substring(0, 8)} · v${s.formVersion}`;
  }
}
